// ============================================================
// ANSICHTSSACHE-AGENT – Listentabelle
// ============================================================
// Die Tabelle einer Listenseite — als KARTE auf dem grauen Grund,
// genau wie die Projektliste (`karten.tsx`): Spaltenkopf auf dem
// Grund, die Zeilen in einer weissen Karte mit Rand und runden
// Ecken, 12px vom Rand, die Summenzeile unten ueber die volle
// Breite. Ansage vom 24.09.2026: „Bitte diese View für alle List
// views verwenden“ — Briefe, Lieferscheine, Ausgaben, Banking und
// jede kuenftige Liste.
//
// Deshalb gibt es fuer Listen nur noch diesen Weg:
//
//   <Listentabelle>
//     <TableHeader>…</TableHeader>
//     <TableBody>…</TableBody>        je Gruppe einer, wenn gruppiert
//     <WeitereZeile … />
//     <tfoot>…</tfoot>
//   </Listentabelle>
//
// Das Markup bleibt das einer Tabelle, die Gestalt kommt aus
// `styles/globals.css` (Abschnitt „Listentabelle“). Eine Zeile mit
// eigenem Grund (Gliederung, Markierung) setzt `--zeilengrund`
// statt einer `bg-…`-Klasse — die Zelle traegt den Grund, damit
// die runden Ecken rund bleiben (`ZEILENGRUND`).
//
// GRUPPIERT eine Liste, bekommt jede Gruppe ihren eigenen
// `TableBody`: jeder Rumpf wird eine eigene Karte, 12px unter der
// vorigen, die erste Zeile traegt den Kopf (`ZEILENGRUND.stufe1`).
// Eine Liste ohne Tabelle nimmt dafuer die Karten aus `karten.tsx`.
// ============================================================

import { Table } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export function Listentabelle({
  className,
  viewportClassName,
  ...props
}: React.ComponentProps<typeof Table>) {
  return (
    <Table
      // Der rollende Kasten ist der Grund: `px-3` rueckt die Karte
      // 12px vom Rand wie den Kartenstapel der Projektliste.
      //
      // Auf dem Telefon traegt die Tabelle den Abstand selbst
      // (`mx-3`), nicht der Kasten: eine klebende Spalte haelt sich
      // an den Innenrand des Rollkastens — mit `px-3` blieben links
      // von ihr 12 Pixel, in denen der Rest der Zeile vorbeirollte.
      viewportClassName={cn("bg-bg-secondary min-h-0 flex-1 px-3 max-md:px-0", viewportClassName)}
      className={cn("listentabelle max-md:mx-3 max-md:w-[calc(100%-1.5rem)]", className)}
      {...props}
    />
  );
}

/**
 * Der Grund einer Zeile mit eigener Bedeutung. Als Variable statt
 * `bg-…`: die Zelle traegt den Grund (sonst stuende er an den
 * runden Ecken eckig hervor), und die Variable erreicht jede Zelle
 * der Zeile.
 */
export const ZEILENGRUND = {
  /**
   * Kopf einer Gruppe — Kunde im Stundenauszug. Derselbe Grund wie
   * der Kopf einer `Ansichtskarte` (`bg-head`): jede Gruppe steht in
   * einem eigenen `TableBody` und ist damit eine eigene Karte.
   */
  stufe1: "[--zeilengrund:var(--color-bg-head)]",
  /** Gliederung, zweite Stufe — Projekt darunter. */
  stufe2:
    "[--zeilengrund:color-mix(in_srgb,var(--color-bg-secondary)_30%,var(--color-bg-primary))]",
  /** Markiert (Kaestchen gesetzt) — bleibt es auch beim Ueberfahren. */
  markiert:
    "[--zeilengrund:var(--color-accent-subtle)] hover:[--zeilengrund:var(--color-accent-subtle)]",
  /**
   * Aufgeschlagen — die Zeile, auf die ein Verweis zeigt
   * (`?buchung=<id>` im Register Zeit eines Projekts). Halb so
   * kraeftig wie „markiert“: sie sagt „hier bist du gelandet“,
   * nicht „ausgewaehlt“, und bleibt neben einem Kaestchen davon
   * unterscheidbar. Dazu die Akzentkante links — dasselbe Zeichen
   * wie am aktiven Eintrag der Seitenleiste.
   */
  aufgeschlagen:
    "[--zeilengrund:color-mix(in_srgb,var(--color-accent-subtle)_55%,var(--color-bg-primary))] hover:[--zeilengrund:color-mix(in_srgb,var(--color-accent-subtle)_55%,var(--color-bg-primary))] [&>td:first-child]:shadow-[inset_3px_0_0_var(--color-accent)]",
} as const;
